import type { RefObject } from "react";
import { Bold, Italic, List, ListOrdered } from "lucide-react";

/** Minimal Frappe-style formatting row. Inserts markdown into the target
    textarea/input around the current selection. Stored as plain text. */
export function ComposerToolbar({ targetRef, value, onChange }: {
  targetRef: RefObject<HTMLTextAreaElement | HTMLInputElement | null>;
  value: string;
  onChange: (v: string) => void;
}) {
  const wrap = (before: string, after = "") => {
    const el = targetRef.current;
    if (!el) return;
    const s = el.selectionStart ?? value.length;
    const e = el.selectionEnd ?? value.length;
    const selected = value.slice(s, e) || "text";
    onChange(value.slice(0, s) + before + selected + after + value.slice(e));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + before.length, s + before.length + selected.length);
    });
  };

  const prefixLines = (prefix: (i: number) => string) => {
    const el = targetRef.current;
    if (!el) return;
    const s = el.selectionStart ?? 0;
    const e = el.selectionEnd ?? value.length;
    const start = value.lastIndexOf("\n", s - 1) + 1;
    const endIdx = value.indexOf("\n", e);
    const block = value.slice(start, endIdx === -1 ? undefined : endIdx);
    const prefixed = block.split("\n").map((l, i) => (l.trim() ? prefix(i) + l : l)).join("\n");
    onChange(value.slice(0, start) + prefixed + (endIdx === -1 ? "" : value.slice(endIdx)));
    requestAnimationFrame(() => el.focus());
  };

  const btn = "rounded-md p-1.5 text-ink-3 hover:bg-soft hover:text-ink";
  return (
    <div className="flex items-center gap-0.5" role="toolbar" aria-label="Formatting">
      <button type="button" title="Bold" onMouseDown={e => e.preventDefault()} onClick={() => wrap("**", "**")} className={btn}>
        <Bold className="h-3.5 w-3.5" />
      </button>
      <button type="button" title="Italic" onMouseDown={e => e.preventDefault()} onClick={() => wrap("*", "*")} className={btn}>
        <Italic className="h-3.5 w-3.5" />
      </button>
      <span className="mx-1 h-4 w-px bg-line" />
      <button type="button" title="Bullet list" onMouseDown={e => e.preventDefault()} onClick={() => prefixLines(() => "- ")} className={btn}>
        <List className="h-3.5 w-3.5" />
      </button>
      <button type="button" title="Numbered list" onMouseDown={e => e.preventDefault()} onClick={() => prefixLines(i => `${i + 1}. `)} className={btn}>
        <ListOrdered className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
